import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  Subscription,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
} from "@/types/breeze/AnythinkPay";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import { PaginatedResult } from "@/types/breeze/Any";
import { useAuth } from "@/providers/AuthProvider";

// Query keys
export const anythinkPaySubscriptionKeys = {
  all: ["anythinkpay-subscriptions"] as const,
  lists: () => [...anythinkPaySubscriptionKeys.all, "list"] as const,
  list: (page: number, pageSize: number, filters: string) =>
    [
      ...anythinkPaySubscriptionKeys.lists(),
      { page, pageSize, filters },
    ] as const,
  details: () => [...anythinkPaySubscriptionKeys.all, "detail"] as const,
  detail: (id: string) =>
    [...anythinkPaySubscriptionKeys.details(), id] as const,
};

// Queries
export function useAnythinkPaySubscriptions(
  page: number = 1,
  pageSize: number = 25,
  filter: string = "",
) {
  return useQuery<PaginatedResult<Subscription>>({
    queryKey: anythinkPaySubscriptionKeys.list(page, pageSize, filter),
    queryFn: () => anythinkPayService.getSubscriptions(page, pageSize, filter),
  });
}

export function useAnythinkPaySubscription(id: string) {
  return useQuery<Subscription>({
    queryKey: anythinkPaySubscriptionKeys.detail(id),
    queryFn: () => anythinkPayService.getSubscription(id),
    enabled: !!id,
  });
}

export function useAnythinkPaySubscriptionsByStatus(
  status: string,
  page: number = 1,
  pageSize: number = 25,
  filter: string = "",
) {
  return useQuery<PaginatedResult<Subscription>>({
    queryKey: anythinkPaySubscriptionKeys.list(
      page,
      pageSize,
      `status:${status}${filter ? `&${filter}` : ""}`,
    ),
    queryFn: () =>
      anythinkPayService.getSubscriptionsByStatus(
        status,
        page,
        pageSize,
        filter,
      ),
    enabled: status !== "all",
  });
}

// Mutations
export function useCreateAnythinkPaySubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSubscriptionRequest) =>
      anythinkPayService.createSubscription(data),
    onSuccess: (response: CreateSubscriptionResponse) => {
      // Invalidate and refetch subscription lists
      queryClient.invalidateQueries({
        queryKey: anythinkPaySubscriptionKeys.lists(),
      });
      toast.success("Subscription created successfully");
      return response;
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create subscription. Please check your input and try again.";
      toast.error(errorMessage);
      console.error("Create subscription error:", error);
    },
  });
}

export function useCancelAnythinkPaySubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => anythinkPayService.cancelSubscription(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: anythinkPaySubscriptionKeys.detail(id),
      });

      // Snapshot the previous value
      const previousSubscription = queryClient.getQueryData<Subscription>(
        anythinkPaySubscriptionKeys.detail(id),
      );

      // Optimistically update to canceled status
      if (previousSubscription) {
        queryClient.setQueryData<Subscription>(
          anythinkPaySubscriptionKeys.detail(id),
          {
            ...previousSubscription,
            status: "canceled",
            auto_cancel_at:
              previousSubscription.current_period_ends_at || undefined,
          },
        );
      }

      return { previousSubscription };
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: anythinkPaySubscriptionKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: anythinkPaySubscriptionKeys.lists(),
      });
      toast.success("Subscription cancelled successfully");
    },
    onError: (error: any, id, context) => {
      // Rollback on error
      if (context?.previousSubscription) {
        queryClient.setQueryData<Subscription>(
          anythinkPaySubscriptionKeys.detail(id),
          context.previousSubscription,
        );
      }
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to cancel subscription. The subscription may already be canceled or you may not have permission.";
      toast.error(errorMessage);
      console.error("Cancel subscription error:", error);
    },
  });
}

export function useResumeAnythinkPaySubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => anythinkPayService.resumeSubscription(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: anythinkPaySubscriptionKeys.detail(id),
      });

      // Snapshot the previous value
      const previousSubscription = queryClient.getQueryData<Subscription>(
        anythinkPaySubscriptionKeys.detail(id),
      );

      // Optimistically update to active status
      if (previousSubscription) {
        queryClient.setQueryData<Subscription>(
          anythinkPaySubscriptionKeys.detail(id),
          {
            ...previousSubscription,
            status: "active",
            auto_cancel_at: undefined,
          },
        );
      }

      return { previousSubscription };
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: anythinkPaySubscriptionKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: anythinkPaySubscriptionKeys.lists(),
      });
      toast.success("Subscription resumed successfully");
    },
    onError: (error: any, id, context) => {
      // Rollback on error
      if (context?.previousSubscription) {
        queryClient.setQueryData<Subscription>(
          anythinkPaySubscriptionKeys.detail(id),
          context.previousSubscription,
        );
      }
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to resume subscription. The subscription may not be canceled or you may not have permission.";
      toast.error(errorMessage);
      console.error("Resume subscription error:", error);
    },
  });
}

export const useSubscriptionUsers = (subscriptionId: string) => {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ["subscription-users", subscriptionId],
    queryFn: () => anythinkPayService.getSubscriptionUsers(subscriptionId),
    enabled: !!currentTenant && !!subscriptionId,
  });
};

export const useUpdateSubscriptionUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscriptionId,
      userId,
      readonly,
    }: {
      subscriptionId: string;
      userId: number;
      readonly: boolean;
    }) => {
      return anythinkPayService.updateSubscriptionUser(subscriptionId, {
        user_id: userId,
        readonly,
      });
    },
    onSuccess: (_, { subscriptionId }) => {
      queryClient.invalidateQueries({
        queryKey: ["subscription-users", subscriptionId],
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update subscription user. You may not have permission.";
      toast.error(errorMessage);
      console.error("Update subscription user error:", error);
    },
  });
};

export const useDeleteSubscriptionUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscriptionId,
      userId,
    }: {
      subscriptionId: string;
      userId: number;
    }) => {
      return anythinkPayService.deleteSubscriptionUser(subscriptionId, userId);
    },
    onSuccess: (_, { subscriptionId }) => {
      queryClient.invalidateQueries({
        queryKey: ["subscription-users", subscriptionId],
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete subscription user. You may not have permission.";
      toast.error(errorMessage);
      console.error("Delete subscription user error:", error);
    },
  });
};

// --- Admin recovery tooling ---

function useRecoveryMutation<TVars>(
  mutationFn: (vars: TVars) => Promise<unknown>,
  fallbackError: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: anythinkPaySubscriptionKeys.lists(),
      });
    },
    onError: (error: any) => {
      const data = error?.response?.data;
      toast.error(
        (typeof data === "string" && data) ||
          data?.message ||
          error?.message ||
          fallbackError,
      );
    },
  });
}

export function useAdminDeleteSubscription() {
  return useRecoveryMutation(
    (id: string) => anythinkPayService.adminDeleteSubscription(id),
    "Failed to delete subscription.",
  );
}

export function useAdminSetSubscriptionStatus() {
  return useRecoveryMutation(
    ({
      id,
      status,
      clearPeriod,
    }: {
      id: string;
      status: string;
      clearPeriod?: boolean;
    }) =>
      anythinkPayService.adminSetSubscriptionStatus(id, status, clearPeriod),
    "Failed to update subscription status.",
  );
}

export function useAdminRelinkSubscription() {
  return useRecoveryMutation(
    ({ id, userId }: { id: string; userId: number }) =>
      anythinkPayService.adminRelinkSubscription(id, userId),
    "Failed to relink subscription.",
  );
}

export function useAdminResyncSubscription() {
  return useRecoveryMutation(
    (id: string) => anythinkPayService.adminResyncSubscription(id),
    "Failed to re-sync subscription.",
  );
}
